import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Share2, Star, Phone, Mail } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { detectVideoKind, youtubeEmbedUrl } from "@/lib/media";
import { projectPhotos, coverSrc } from "@/lib/gallery";
import Lightbox from "@/components/site/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    axios
      .get(`${API}/projects/${slug}`)
      .then((r) => !cancelled && setState({ loading: false, data: r.data, error: null }))
      .catch((e) => !cancelled && setState({ loading: false, data: null, error: e?.response?.status || 500 }));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] text-[#0A0E1A] flex items-center justify-center">
        <div className="text-[#6B7280] font-display tracking-widest uppercase text-sm">Loading…</div>
      </div>
    );
  }

  if (state.error === 404 || !state.data?.project) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] text-[#0A0E1A]">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="project-not-found">
          <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">404</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mb-6">Project Not Found</h1>
          <p className="text-[#4B5563] mb-8">This project may have been removed or the link is incorrect.</p>
          <Link to="/#gallery" className="inline-flex items-center gap-2 bg-[#84FB00] hover:bg-[#6EE500] px-6 py-3 font-display tracking-widest uppercase text-[#0A0E1A]">
            <ArrowLeft size={14} /> Back to Portfolio
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { project, contact = {}, prev, next } = state.data;
  const photos = projectPhotos(project);
  const img = coverSrc(project) || project.image_data || project.image_url;
  const videoKind = detectVideoKind(project.video_url);
  const siteOrigin =
    (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "") || window.location.origin;
  const currentUrl = `${siteOrigin}/project/${project.slug}`;
  const description =
    project.featured_quote ||
    project.alt ||
    `${project.label} by LC Builders LLC — metal buildings, carports, fences, and welding in ${contact.area || "Central Texas"}.`;

  // Photo slides for the lightbox (video, if any, only rides on the cover slide).
  const photoSlides = photos.map((p, i) => ({
    ...project,
    image_data: p.image_data,
    image_url: p.image_url || "",
    video_url: i === 0 ? project.video_url : "",
    _project_id: project.id,
    _photo_index: i,
    _photo_count: photos.length,
  }));

  const doShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `LC Builders — ${project.label}`, url: currentUrl });
        return;
      } catch {
        /* cancelled */
      }
    }
    try {
      await navigator.clipboard.writeText(currentUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Unable to copy — " + currentUrl);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-[#0A0E1A]" data-testid="project-page">
      <Helmet>
        <title>{`${project.label} — LC Builders LLC`}</title>
        <meta name="description" content={description.slice(0, 160)} />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${project.label} — LC Builders LLC`} />
        <meta property="og:description" content={description.slice(0, 200)} />
        <meta property="og:url" content={currentUrl} />
        {img && !img.startsWith("data:") && <meta property="og:image" content={img} />}
        <meta property="og:site_name" content="LC Builders LLC" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${project.label} — LC Builders LLC`} />
        <meta name="twitter:description" content={description.slice(0, 200)} />
        {img && !img.startsWith("data:") && <meta name="twitter:image" content={img} />}
        <link rel="canonical" href={currentUrl} />
      </Helmet>

      <Header />

      <main>
        {/* Hero image */}
        <section className="relative border-b border-[#E5E7EB]">
          <div className="relative min-h-[60vh] md:min-h-[75vh] overflow-hidden bg-[#0d0d0d]">
            {videoKind === "mp4" ? (
              <video
                src={project.video_url}
                poster={img}
                autoPlay
                muted
                loop
                playsInline
                data-testid="project-video"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : videoKind === "youtube" ? (
              <iframe
                title={project.label}
                src={youtubeEmbedUrl(project.video_url, { background: true })}
                data-testid="project-iframe"
                allow="autoplay; encrypted-media"
                className="absolute inset-0 w-full h-full border-0"
              />
            ) : img ? (
              <img src={img} alt={project.alt || project.label} className="absolute inset-0 w-full h-full object-cover" />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-[#F5F7FA] via-[#121212]/40 to-[#F5F7FA]/20" />
            <div className="absolute left-0 top-0 h-full w-2 stripe-accent hidden md:block" />

            {project.featured && (
              <div className="absolute top-6 md:top-8 left-6 md:left-12 inline-flex items-center gap-2 bg-[#84FB00] px-4 py-2">
                <Star size={14} className="fill-white text-[#0A0E1A]" />
                <span className="font-display tracking-[0.25em] uppercase text-xs">Featured Project</span>
              </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
              <div className="max-w-7xl mx-auto">
                <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">PROJECT</div>
                <h1 data-testid="project-title" className="font-display uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tight text-[#0A0E1A]">
                  {project.label}
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            {project.featured_quote ? (
              <div className="border-l-2 border-[#84FB00] pl-6 mb-12">
                <p className="text-xl md:text-2xl text-white/85 leading-relaxed italic">"{project.featured_quote}"</p>
                {project.featured_author && (
                  <p className="mt-4 font-display tracking-widest uppercase text-sm text-[#4B5563]">— {project.featured_author}</p>
                )}
              </div>
            ) : project.alt ? (
              <p className="text-lg md:text-xl text-[#1F2937] leading-relaxed mb-12">{project.alt}</p>
            ) : null}

            {photos.length > 1 && (
              <div className="mb-12" data-testid="project-photo-grid">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-display tracking-[0.3em] text-xs text-[#4AB300]">
                    {photos.length} PHOTOS
                  </div>
                  <div className="text-xs text-[#9CA3AF] font-display tracking-widest uppercase hidden sm:block">
                    Tap any photo to expand
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                  {photos.map((p, i) => {
                    const src = p.image_data || p.image_url;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightboxIdx(i)}
                        data-testid={`project-photo-${i}`}
                        className="relative aspect-square overflow-hidden border border-[#E5E7EB] hover:border-[#84FB00] focus:border-[#84FB00] focus:outline-none group bg-[#0d0d0d]"
                        aria-label={`Open photo ${i + 1} of ${photos.length}`}
                      >
                        {src ? (
                          <img
                            src={src}
                            alt={`${project.label} — photo ${i + 1}`}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white/30 text-xs">No image</div>
                        )}
                        <div className="absolute bottom-2 right-2 bg-white/60 text-[#0A0E1A] text-[10px] font-display tracking-widest uppercase px-2 py-0.5">
                          {i + 1} / {photos.length}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pb-12 border-b border-[#E5E7EB]">
              <button
                onClick={doShare}
                data-testid="project-share"
                className="inline-flex items-center gap-2 bg-[#84FB00] hover:bg-[#6EE500] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm"
              >
                <Share2 size={14} /> Share This Project
              </button>
              {contact.phone_link && (
                <a
                  href={`tel:${contact.phone_link}`}
                  className="inline-flex items-center gap-2 border border-white/25 hover:border-[#84FB00] hover:text-[#4AB300] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm transition-colors"
                >
                  <Phone size={14} /> {contact.phone}
                </a>
              )}
              {contact.email && (
                <a
                  href={`mailto:${contact.email}`}
                  className="inline-flex items-center gap-2 border border-white/25 hover:border-[#84FB00] hover:text-[#4AB300] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm transition-colors"
                >
                  <Mail size={14} /> Email
                </a>
              )}
            </div>

            {/* Prev/Next */}
            <div className="mt-8 grid grid-cols-2 gap-6">
              {prev ? (
                <Link
                  to={`/project/${prev.slug}`}
                  data-testid="project-prev"
                  className="group border border-[#E5E7EB] p-6 hover:border-[#84FB00] transition-colors"
                >
                  <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-[#6B7280] font-display group-hover:text-[#4AB300]">
                    <ArrowLeft size={14} /> Previous
                  </div>
                  <div className="font-display uppercase text-xl md:text-2xl tracking-wide mt-2 text-[#0A0E1A]">
                    {prev.label}
                  </div>
                </Link>
              ) : (
                <div />
              )}
              {next ? (
                <Link
                  to={`/project/${next.slug}`}
                  data-testid="project-next"
                  className="group border border-[#E5E7EB] p-6 text-right hover:border-[#84FB00] transition-colors"
                >
                  <div className="flex items-center justify-end gap-2 text-xs tracking-widest uppercase text-[#6B7280] font-display group-hover:text-[#4AB300]">
                    Next <ArrowRight size={14} />
                  </div>
                  <div className="font-display uppercase text-xl md:text-2xl tracking-wide mt-2 text-[#0A0E1A]">
                    {next.label}
                  </div>
                </Link>
              ) : (
                <div />
              )}
            </div>

            <div className="mt-16 text-center">
              <Link
                to="/#gallery"
                data-testid="project-back"
                className="inline-flex items-center gap-2 text-[#4B5563] hover:text-[#4AB300] font-display tracking-widest uppercase text-sm transition-colors"
              >
                <ArrowLeft size={14} /> Back to Portfolio
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {lightboxIdx !== null && (
        <Lightbox
          items={photoSlides}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onIndexChange={setLightboxIdx}
        />
      )}
    </div>
  );
}
